import { apiClient } from '@/api/client';
import {
  AdminBusinessStatusUpdateDTOStatus,
  AdminBusinessUpdateDTOStatus,
  Business,
  UserEventCreateDTOEntityType,
  UserEventCreateDTOEventType,
} from '@/api/models';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useTrackEventSimple } from '@/lib/trackingService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

export interface BusinessFormData {
  name: string;
  address: string;
  postalCode: string;
  city: string;
  province: string;
  country: string;
  website: string;
  phone: string;
  status: string;
  categoryIds: number[];
  filterIds: number[];
  openingHours: Array<{
    dayOfWeek: number;
    openTime: string;
    closeTime: string;
  }>;
}

export interface BusinessMutationsActions {
  handleFormSubmit: (
    formData: BusinessFormData,
    selectedBusiness: Business | null,
  ) => Promise<void>;
  handleDeleteBusiness: (business: Business) => void;
  handleStatusChange: (business: Business, status: string) => void;
  handleToggleReview: (
    businessId: number,
    reviewId: number,
    isEvaluated: boolean,
  ) => void;
  handleDeleteImage: (businessId: number, imageId: number) => void;
  handleSetPrimaryImage: (businessId: number, imageId: number) => void;
}

export const useBusinessMutations = () => {
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useTrackEventSimple();

  const updateBusinessMutation = useMutation({
    mutationFn: async (
      data: BusinessFormData & { id: number; originalBusiness?: Business },
    ) => {
      const response = await apiClient.PUT('/admin/businesses/{id}', {
        params: {
          path: { id: data.id },
        },
        body: {
          name: data.name,
          address: data.address,
          postalCode: data.postalCode,
          city: data.city,
          province: data.province,
          country: data.country,
          website: data.website || undefined,
          phone: data.phone || undefined,
          status: data.status as AdminBusinessUpdateDTOStatus,
          categoryIds: data.categoryIds,
          filterIds: data.filterIds,
          openingHours: data.openingHours,
        },
      });

      if (response.error) {
        throw new Error('Failed to update business');
      }

      // Calculate changed fields for tracking
      const changedFields: Record<string, string | number | boolean> = {};
      const original = data.originalBusiness;
      if (original) {
        if (data.name !== original.name) changedFields.name = data.name;
        if (data.address !== original.address)
          changedFields.address = data.address;
        if (data.city !== original.city) changedFields.city = data.city;
        if (data.country !== original.country)
          changedFields.country = data.country;
        if (data.status !== original.status) changedFields.status = data.status;
        if (data.phone !== original.phone) changedFields.phone = data.phone;
        if (data.website !== original.website)
          changedFields.website = data.website;
      }

      return { data: response.data, id: data.id, changedFields };
    },
    onSuccess: result => {
      trackEvent({
        eventType: UserEventCreateDTOEventType.admin_business_updated,
        entityType: UserEventCreateDTOEntityType.business,
        entityId: result.id,
        properties: result.changedFields,
      });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Business updated successfully',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESSES] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESS] });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to update business',
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (data: {
      id: number;
      status: string;
      previousStatus?: string;
    }) => {
      const response = await apiClient.PUT('/admin/businesses/{id}/status', {
        params: {
          path: { id: data.id },
        },
        body: {
          status: data.status as AdminBusinessStatusUpdateDTOStatus,
        },
      });

      if (response.error) {
        throw new Error('Failed to update business status');
      }

      return {
        data: response.data,
        id: data.id,
        status: data.status,
        previousStatus: data.previousStatus,
      };
    },
    onSuccess: result => {
      trackEvent({
        eventType: UserEventCreateDTOEventType.admin_business_status_changed,
        entityType: UserEventCreateDTOEntityType.business,
        entityId: result.id,
        properties: {
          before: result.previousStatus || '',
          after: result.status,
        },
      });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Business status updated successfully',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESSES] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESS] });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to update business status',
      });
    },
  });

  const deleteBusinessMutation = useMutation({
    mutationFn: async (data: { id: number; name: string }) => {
      const response = await apiClient.DELETE('/admin/businesses/{id}', {
        params: {
          path: { id: data.id },
        },
      });

      if (response.error) {
        throw new Error('Failed to delete business');
      }

      return { id: data.id, name: data.name };
    },
    onSuccess: result => {
      trackEvent({
        eventType: UserEventCreateDTOEventType.admin_business_deleted,
        entityType: UserEventCreateDTOEntityType.business,
        entityId: result.id,
      });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Business deleted successfully',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESSES] });
      navigate('/businesses');
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to delete business',
      });
    },
  });

  const toggleReviewMutation = useMutation({
    mutationFn: async (data: {
      businessId: number;
      reviewId: number;
      isEvaluated: boolean;
    }) => {
      const response = await apiClient.PUT(
        '/admin/businesses/{id}/reviews/{reviewId}',
        {
          params: {
            path: { id: data.businessId, reviewId: data.reviewId },
          },
          body: {
            isEvaluated: data.isEvaluated,
          },
        },
      );

      if (response.error) {
        throw new Error('Failed to toggle review visibility');
      }

      return { ...data };
    },
    onSuccess: result => {
      trackEvent({
        eventType: UserEventCreateDTOEventType.admin_review_visibility_changed,
        entityType: UserEventCreateDTOEntityType.review,
        entityId: result.reviewId,
        properties: {
          businessId: result.businessId,
          isEvaluated: result.isEvaluated,
        },
      });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Review visibility updated',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESS] });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to toggle review visibility',
      });
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: async (data: { businessId: number; imageId: number }) => {
      const response = await apiClient.DELETE(
        '/admin/businesses/{id}/images/{imageId}' as '/admin/businesses/{id}',
        {
          params: {
            path: { id: data.businessId, imageId: data.imageId } as {
              id: number;
            },
          },
        },
      );

      if (response.error) {
        throw new Error('Failed to delete image');
      }

      return data;
    },
    onSuccess: result => {
      trackEvent({
        eventType: UserEventCreateDTOEventType.admin_business_updated,
        entityType: UserEventCreateDTOEntityType.business,
        entityId: result.businessId,
        properties: {
          action: 'image_deleted',
          imageId: result.imageId,
        },
      });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Image deleted successfully',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESS] });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to delete image',
      });
    },
  });

  const setPrimaryImageMutation = useMutation({
    mutationFn: async (data: { businessId: number; imageId: number }) => {
      const response = await apiClient.PUT(
        '/admin/businesses/{id}/images/{imageId}/primary' as '/admin/businesses/{id}',
        {
          params: {
            path: { id: data.businessId, imageId: data.imageId } as {
              id: number;
            },
          },
        },
      );

      if (response.error) {
        throw new Error('Failed to set primary image');
      }

      return data;
    },
    onSuccess: result => {
      trackEvent({
        eventType: UserEventCreateDTOEventType.admin_business_updated,
        entityType: UserEventCreateDTOEntityType.business,
        entityId: result.businessId,
        properties: {
          action: 'primary_image_changed',
          imageId: result.imageId,
        },
      });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Primary image updated',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESS] });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to set primary image',
      });
    },
  });

  // Action handlers
  const handleFormSubmit = async (
    formData: BusinessFormData,
    selectedBusiness: Business | null,
  ) => {
    try {
      if (selectedBusiness && selectedBusiness.id) {
        await updateBusinessMutation.mutateAsync({
          ...formData,
          id: selectedBusiness.id,
          originalBusiness: selectedBusiness,
        });
        navigate(`/businesses/${selectedBusiness.id}`);
      }
    } catch {
      // Error handling is done in the mutations
    }
  };

  const handleDeleteBusiness = (business: Business) => {
    if (window.confirm(`Are you sure you want to delete "${business.name}"?`)) {
      deleteBusinessMutation.mutate({ id: business.id!, name: business.name });
    }
  };

  const handleStatusChange = (business: Business, status: string) => {
    updateStatusMutation.mutate({
      id: business.id!,
      status,
      previousStatus: business.status,
    });
  };

  const handleToggleReview = (
    businessId: number,
    reviewId: number,
    isEvaluated: boolean,
  ) => {
    toggleReviewMutation.mutate({ businessId, reviewId, isEvaluated });
  };

  const handleDeleteImage = (businessId: number, imageId: number) => {
    deleteImageMutation.mutate({ businessId, imageId });
  };

  const handleSetPrimaryImage = (businessId: number, imageId: number) => {
    setPrimaryImageMutation.mutate({ businessId, imageId });
  };

  const actions: BusinessMutationsActions = {
    handleFormSubmit,
    handleDeleteBusiness,
    handleStatusChange,
    handleToggleReview,
    handleDeleteImage,
    handleSetPrimaryImage,
  };

  const isFormLoading = updateBusinessMutation.isPending;
  const isDeleting = deleteBusinessMutation.isPending;
  const isStatusUpdating = updateStatusMutation.isPending;
  const isTogglingReview = toggleReviewMutation.isPending;
  const isDeletingImage = deleteImageMutation.isPending;
  const isSettingPrimaryImage = setPrimaryImageMutation.isPending;

  return {
    actions,
    isFormLoading,
    isDeleting,
    isStatusUpdating,
    isTogglingReview,
    isDeletingImage,
    isSettingPrimaryImage,
  };
};
